import { useEffect, useState } from 'react';

import { applyRemoteWhiteLabelConfig, clearRemoteWhiteLabelConfig } from 'config/whiteLabelDomains';
import { getWhiteLabelClientByDomain } from 'services/whiteLabelApi';

// Hostnames that can never have a white-label row; skip the backend lookup there.
// vercel.app covers preview deployments and the stable per-client aliases, which
// stay on the static config (backend rows are keyed to the real customer domains).
const SKIPPED_HOST_SUFFIXES = ['getthera.com', 'carbon-benefits.com', 'vercel.app', 'localhost'];

function shouldSkipLookup(host: string): boolean {
  const bare = host.toLowerCase();
  return bare === '127.0.0.1' || SKIPPED_HOST_SUFFIXES.some((suffix) => bare === suffix || bare.endsWith(`.${suffix}`));
}

// The row's asset URLs are ~1h signed URLs, so refresh at half that validity to keep a
// long SPA session from re-requesting an expired URL when a logo remounts on navigation.
const REFRESH_INTERVAL_MS = 30 * 60 * 1000;
// If refreshes keep failing, drop the override before its signed URLs expire so assets
// revert to the never-expiring static paths instead of 403ing.
const STALE_OVERRIDE_MS = 50 * 60 * 1000;

/**
 * Hydrates white-label branding from the backend, the source of truth for branding rows.
 * The static config keeps the first paint correct for known clients and remains the
 * fallback when the backend is unreachable. Returns a version number that bumps when
 * hydration changed the effective branding, so callers can re-render with the new values.
 * Refreshes periodically (and on tab focus after a gap) so the signed asset URLs in the
 * row never expire mid-session.
 */
export function useWhiteLabelRemoteConfig(): number {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const host = window.location.hostname;
    if (shouldSkipLookup(host)) {
      return;
    }
    let cancelled = false;
    let lastFetchedAt = 0;
    let lastAppliedAt = 0;

    const hydrate = async () => {
      lastFetchedAt = Date.now();
      try {
        const client = await getWhiteLabelClientByDomain(host);
        if (cancelled) {
          return;
        }
        if (!client) {
          // The row is gone; revert to the static entry rather than serving a stale override.
          lastAppliedAt = 0;
          if (clearRemoteWhiteLabelConfig()) {
            setVersion((v) => v + 1);
          }
          return;
        }
        lastAppliedAt = Date.now();
        if (applyRemoteWhiteLabelConfig(client)) {
          setVersion((v) => v + 1);
        }
      } catch {
        // Backend unreachable: static branding stays in effect. If an override is close to
        // its signed-URL expiry and cannot be refreshed, drop it so assets fall back too.
        if (lastAppliedAt && Date.now() - lastAppliedAt > STALE_OVERRIDE_MS) {
          lastAppliedAt = 0;
          if (clearRemoteWhiteLabelConfig()) {
            setVersion((v) => v + 1);
          }
        }
      }
    };

    hydrate();
    const interval = window.setInterval(hydrate, REFRESH_INTERVAL_MS);
    // Timers do not fire on a sleeping laptop; catch up when the tab becomes visible again.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchedAt > REFRESH_INTERVAL_MS) {
        hydrate();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return version;
}
